import { useCallback } from "react";
import { Outlet } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import useAnalytics from "hooks/useAnalytics";
import useFavicon from "hooks/useFavicon";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { VersionState } from "types/generated";
import YankedBadge from "views/Account/Module/Versions/Components/YankedBadge";
import { EMPTY_COMMIT_MESSAGE_VERSION_NAME } from "views/Account/Module/Versions/constants";
import PermissionGateDropdownItem from "components/PermissionGate/DropdownMenuItem";

import { VersionContext } from "../../Context";
import { showModuleVersionAddDescriptionDrawer } from "../AddDescriptionDrawer";
import DeleteButton from "../DeleteButton";
import { showModuleVersionDetailsDrawer } from "../DetailsDrawer";
import MarkAsBadDropdownItem from "../MarkAsBad/DropdownItem";
import ModuleVersionMarkedAsBadCallout from "../MarkedAsBadCallout";
import { showModuleVersionMarkedAsBadNoteDrawer } from "../MarkedAsBadNoteDrawer";
import VersionStateBadge from "../StateBadge";
import TriggerAgainDropdownItem from "../TriggerAgainDropdownItem";
import VersionDropdown from "../VersionDropdown";

const ModuleVersionMainLayout = () => {
  const { version, module, canManageModule, canMarkModuleAsBad, canDeleteModuleVersion } =
    useTypedContext(VersionContext);

  const showDescriptionDrawer = () => {
    showModuleVersionAddDescriptionDrawer({
      moduleId: module.id,
      versionId: version.id,
      description: version.notes,
    });
  };

  const handleOpenDetailsDrawer = () => {
    showModuleVersionDetailsDrawer({
      moduleProvider: module.provider,
      moduleVcsIntegrationId: module.vcsIntegration?.id,
      version,
    });
  };

  const handleOpenMarkedAsBadNoteDrawer = () => {
    showModuleVersionMarkedAsBadNoteDrawer({
      note: version.yankNote,
    });
  };

  useBreadcrumbs(
    [
      {
        title: "Modules",
        link: "/modules",
      },
      {
        title: module.id,
        link: `/module/${module.id}`,
      },
      {
        title: version.commit?.message || "",
      },
    ],
    [version.number, version.commit?.message, module.id]
  );

  useTitle(`${module.id} · ${version.number}`);

  useFavicon(version.yanked ? VersionState.Failed : version.state);

  const trackSegmentAnalyticsEvent = useAnalytics();

  const handleDropdownOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        trackSegmentAnalyticsEvent("Module Registry - Version Dropdown Opened");
      }
    },
    [trackSegmentAnalyticsEvent]
  );

  const handleClick = useCallback(
    (button: string, callback?: () => void) => () => {
      trackSegmentAnalyticsEvent("Module Registry - Version Dropdown Clicked", {
        button,
      });

      callback?.();
    },
    [trackSegmentAnalyticsEvent]
  );

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper justify="between" fullWidth>
          <ViewHeaderWrapper align="center">
            <ViewHeaderTitle>
              {version?.commit?.message || EMPTY_COMMIT_MESSAGE_VERSION_NAME}
            </ViewHeaderTitle>
            <VersionStateBadge state={version.state} text={version.number} />
            {version.yanked && <YankedBadge notes={version.yankNote} />}
          </ViewHeaderWrapper>

          <ViewHeaderWrapper>
            <VersionDropdown
              moduleId={module.id}
              versionNumber={version.number}
              isLatest={version.number === module.current?.number}
            />
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>
            <DropdownMenuEllipsis
              dotsSize="medium"
              tooltip="Version actions"
              onOpenChange={handleDropdownOpenChange}
            >
              {version.state === VersionState.Failed && (
                <TriggerAgainDropdownItem
                  canManageModule={canManageModule}
                  id={version.id}
                  moduleId={module.id}
                />
              )}
              <CopyFieldDropdownMenuItem
                title="Copy module ID"
                value={module.id}
                onCopy={handleClick("Copy module ID")}
              />
              <CopyFieldDropdownMenuItem
                title="Copy version ID"
                value={version.id}
                onCopy={handleClick("Copy version ID")}
              />
              <PermissionGateDropdownItem
                access={canManageModule}
                onAction={handleClick("Edit description", () => showDescriptionDrawer())}
              >
                Edit description
              </PermissionGateDropdownItem>
              {!version.yanked && (
                <MarkAsBadDropdownItem
                  canMarkModuleAsBad={canMarkModuleAsBad}
                  id={version.id}
                  moduleId={module.id}
                  versionState={version.state}
                  versionNumber={version.number}
                  onClickCb={handleClick("Mark version as bad")}
                />
              )}
              <DeleteButton
                canDeleteModuleVersion={canDeleteModuleVersion}
                id={version.id}
                moduleId={module.id}
                number={version.number}
                onClickCb={handleClick("Delete version")}
              />
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>
        <ViewHeaderNavigation>
          <Tab to="../overview" label="Overview" />
          <TabWithCounter
            count={version.metadata?.submodules?.length || 0}
            to="../submodules"
            label="Submodules"
          />
          <TabWithCounter
            count={version.metadata?.examples?.length || 0}
            to="../examples"
            label="Examples"
          />
        </ViewHeaderNavigation>
      </ViewHeader>
      {version.yanked && (
        <ModuleVersionMarkedAsBadCallout onShowMore={handleOpenMarkedAsBadNoteDrawer} />
      )}
      <Outlet />
    </>
  );
};

export default ModuleVersionMainLayout;
